/**
 * The location the header shows must be the location the session is on.
 *
 * Permissions come from the JWT cookie's location, never from the header:
 * the server loads the person's roles AT THE TOKEN'S LOCATION. The header's
 * selection, though, is restored at startup from ?co= or localStorage — which
 * is per BROWSER, not per person — and restoring it only moves the label.
 * So the header could say "Mercedes Benz of Buffalo" (Ownership: may edit
 * timesheets) while every request carried the login location (Team Leader:
 * may not), and gated UI like the Time Clock "Edit" tab silently vanished.
 * (prod, 2026-09-29: an account admin whose first location sorted to a shop
 * where they were only Team Leader.)
 *
 * This hook tracks where the session actually is — the token's location from
 * /auth/me, then every successful switch — and when the selection differs it
 * performs a real switch to the selected location. Each mismatched selection
 * is tried once, so a refused switch cannot loop; a refused switch shows the
 * session's own location instead, so the header never claims another one.
 */
import { useCallback, useEffect, useRef } from 'react';

/** ok = session moved; failed = refused; busy = another switch was running. */
export type LocationSwitchOutcome = 'ok' | 'failed' | 'busy';

export interface SessionLocationSyncOptions {
  /** Signed in, with the user (and so the token's location) loaded. */
  enabled: boolean;
  /** The token's location as /auth/me reports it; null = account view. */
  tokenLocationId: string | null | undefined;
  /** The location the header shows. */
  selectedLocationId: string | null;
  /** A session switch is already running; wait for it. */
  switchInFlight: boolean;
  /** Real switch: moves the cookie, permissions and selection. */
  switchToLocation: (locationId: string) => Promise<LocationSwitchOutcome>;
  /** Moves the header only — used to show the session's location after a refusal. */
  showLocation: (locationId: string | null) => void;
}

export function useSessionLocationSync({
  enabled,
  tokenLocationId,
  selectedLocationId,
  switchInFlight,
  switchToLocation,
  showLocation,
}: SessionLocationSyncOptions) {
  // undefined = not known yet; null = the session has no location (account view).
  const sessionLocationRef = useRef<string | null | undefined>(undefined);
  const attemptedRef = useRef<string | null>(null);

  // Signed out: forget this session, so the next person to sign in on this
  // tab starts from their own token's location, not the last person's.
  if (!enabled) {
    sessionLocationRef.current = undefined;
    attemptedRef.current = null;
  } else if (sessionLocationRef.current === undefined && tokenLocationId !== undefined) {
    sessionLocationRef.current = tokenLocationId;
  }

  /** Call wherever the session is re-scoped, with the location it landed on. */
  const recordSessionLocation = useCallback((locationId: string | null | undefined) => {
    sessionLocationRef.current = locationId ?? null;
  }, []);

  useEffect(() => {
    if (!enabled || switchInFlight || !selectedLocationId) return;
    if (sessionLocationRef.current === undefined) return;
    if (selectedLocationId === sessionLocationRef.current) return;
    if (attemptedRef.current === selectedLocationId) return;
    attemptedRef.current = selectedLocationId;
    const target = selectedLocationId;
    void switchToLocation(target).then((outcome) => {
      if (outcome === 'busy') {
        // Not refused, just queued behind another switch: allow a retry.
        if (attemptedRef.current === target) attemptedRef.current = null;
        return;
      }
      if (outcome === 'failed') {
        const session = sessionLocationRef.current;
        if (session) showLocation(session);
      }
    });
  }, [enabled, switchInFlight, selectedLocationId, tokenLocationId, switchToLocation, showLocation]);

  return { recordSessionLocation };
}
